// ============================================================================
// Pig Dailys active-group authenticated data owner
// ============================================================================
//
// /webform-pigs is a login-required surface. Its active_groups lookup belongs
// to the direct Pig Dailys selector, but the public-webform presenter must not
// choose an authenticated scope or query webform_config itself. The app-side
// bridge consumes this component-scoped owner and injects only the narrow
// option projection the presenter renders.
//
// This remains deliberately local: it is neither a route-catalog resource nor
// evidence of permanent ownership or boot release. WebformHub keeps its
// independent six-row configuration request for /dailys/*.
// ============================================================================

import {skipToken, useQuery} from '@tanstack/react-query';
import type {QueryKey as TanStackQueryKey, UseQueryOptions} from '@tanstack/react-query';
import {useMemo} from 'react';

import {PROFILE_STATUS, useAuth} from '../../contexts/AuthContext.jsx';
import type {AuthState, ProfileStatus} from '../../contexts/AuthContext.jsx';
import {resourceKey, scopeOrNull, UNRESOLVED_RESOURCE_KEY} from './routeDataCatalog.js';
import type {AuthenticatedScope, QueryKey} from './routeDataCatalog.js';
import {sb} from '../../lib/supabase.js';
import type {SupabaseClient} from '../../lib/supabaseTypes.js';

export const PIG_DAILYS_ACTIVE_GROUPS_RESOURCE = Object.freeze({
  OPTIONS: 'webform.pig-dailys.active-groups',
});

// The historical App sidecar mapped every array member to this object shape
// before the presenter narrowed it for <option>. Keep the raw values unknown
// here so the presenter retains that exact final validation boundary.
export type PigDailysActiveGroup = {readonly value: unknown; readonly label: unknown};

const EMPTY_ACTIVE_PIG_GROUPS: readonly PigDailysActiveGroup[] = Object.freeze([]);

type AbortableRequest = {
  then: unknown;
  abortSignal?: (signal: AbortSignal) => AbortableRequest;
};

function withSignal<Request extends AbortableRequest>(request: Request, signal?: AbortSignal): Request {
  return signal && typeof request.abortSignal === 'function' ? (request.abortSignal(signal) as Request) : request;
}

/**
 * A valid active-groups request needs the same resolved authenticated identity
 * and positive session generation that fence other shared component owners.
 * A role preview remains part of scope, so changing it cannot reuse a prior
 * observer's farm data under an identical cache key.
 */
export function pigDailysActiveGroupsScope(
  authState: AuthState,
  rolePreview: unknown,
  profileStatus: ProfileStatus,
  sessionEpoch: unknown,
): AuthenticatedScope | null {
  if (profileStatus !== PROFILE_STATUS.RESOLVED) return null;
  if (typeof sessionEpoch !== 'number' || !Number.isSafeInteger(sessionEpoch) || sessionEpoch <= 0) return null;
  const scope = scopeOrNull(authState, rolePreview);
  return scope && scope.kind === 'authenticated' ? scope : null;
}

/** Key by identity plus boot epoch, without inventing a public namespace. */
export function pigDailysActiveGroupsQueryKey(scope: AuthenticatedScope, sessionEpoch: number): QueryKey {
  return resourceKey(PIG_DAILYS_ACTIVE_GROUPS_RESOURCE.OPTIONS, scope, {sessionEpoch});
}

/** The sole automatic active_groups read for the direct Pig Dailys form. */
export async function loadPigDailysActiveGroups(
  client: SupabaseClient,
  signal?: AbortSignal,
): Promise<readonly PigDailysActiveGroup[]> {
  const response = await withSignal(
    client.from('webform_config').select('data').eq('key', 'active_groups'),
    signal,
  ).maybeSingle();
  if (response.error) throw new Error(`active pig groups: ${response.error.message || response.error}`);

  const groups = response.data?.data;
  return Array.isArray(groups)
    ? groups.map((name) => Object.freeze({value: name, label: name}))
    : EMPTY_ACTIVE_PIG_GROUPS;
}

type ActivePigGroupsQueryOption = UseQueryOptions<
  readonly PigDailysActiveGroup[],
  Error,
  readonly PigDailysActiveGroup[],
  TanStackQueryKey
>;

export type PigDailysActiveGroupsProjection = {
  readonly groups: readonly PigDailysActiveGroup[];
  readonly loading: boolean;
  readonly error: unknown;
};

/**
 * Scoped owner consumed only by PigDailysAuthenticatedBridge. Failures retain
 * the form's historical empty selector fallback; they remain Query state for
 * a future app-side presenter without turning this local lookup into a
 * route-level gate.
 */
export function usePigDailysActiveGroups({
  client = sb,
}: {client?: SupabaseClient} = {}): PigDailysActiveGroupsProjection {
  const {authState, rolePreview, profileStatus, sessionEpoch} = useAuth();
  const scope = useMemo(
    () => pigDailysActiveGroupsScope(authState, rolePreview, profileStatus, sessionEpoch),
    [authState, profileStatus, rolePreview, sessionEpoch],
  );
  const ready = Boolean(scope);
  const options = useMemo<ActivePigGroupsQueryOption>(() => {
    if (!scope) return {queryKey: UNRESOLVED_RESOURCE_KEY, queryFn: skipToken, enabled: false};
    return {
      queryKey: pigDailysActiveGroupsQueryKey(scope, sessionEpoch),
      // Reading Query's signal is what cancels an orphaned direct-form request.
      queryFn: ({signal}) => loadPigDailysActiveGroups(client, signal),
      enabled: true,
    };
  }, [client, scope, sessionEpoch]);
  const query = useQuery(options);

  return {
    groups: query.data || EMPTY_ACTIVE_PIG_GROUPS,
    loading: Boolean(ready && (query.isPending || (query.isFetching && query.data === undefined))),
    error: ready ? (query.error ?? null) : null,
  };
}
